import { theme } from '@Guidelines/Theme/theme';
import { eyebrowTitleStyle } from '@UI/Pages/Home/Shared/shortcutCardStyles';
import { css } from '@emotion/react';
import VisuallyHiddenText from '@UI/Pages/Home/Shared/VisuallyHiddenText';
import { Info } from 'lucide-react';
import * as React from 'react';
import { Tooltip, type TooltipStyle } from '@verclam/ui-kit';

const PADDING = '1.375rem';

const cardStyle = css`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 1.125rem;
	min-width: 0;
	padding: ${PADDING};
	border-radius: ${theme.radii.xlarge};
	border: 1.5px solid ${theme.colors.border};
	background-color: ${theme.colors.surface};
`;

const bandStyle = css`
	display: flex;
	flex: none;
	height: 5px;
	margin: calc(-1 * ${PADDING}) calc(-1 * ${PADDING}) 0;
	border-radius: ${theme.radii.large} ${theme.radii.large} 0 0;
	overflow: hidden;
`;

const bandSegmentStyle = (color: string) => css`
	flex: 1;
	background-color: ${color};
`;

const headerStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
`;

const titleRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.375rem;
`;

const infoButtonStyle = css`
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	padding: 0;
	border: none;
	border-radius: ${theme.radii.pill};
	background: transparent;
	color: ${theme.colors.inkMuted};
	cursor: help;
	transition: background-color ${theme.motion.duration}, color ${theme.motion.duration};

	&:hover {
		background-color: ${theme.colors.surfaceMuted};
		color: ${theme.colors.ink};
	}

	&:focus-visible {
		outline: 3px solid ${theme.colors.accent};
		outline-offset: 2px;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const bubbleStyle = {
	'--ui-kit-tooltip-max-width': '22rem',
	'--ui-kit-tooltip-bg': theme.colors.surfaceInverted,
	'--ui-kit-tooltip-text': theme.colors.surface,
	'--ui-kit-tooltip-border': theme.colors.surfaceInverted,
	'--ui-kit-tooltip-font-size': '0.78125rem',
} as TooltipStyle;

const bubbleTextStyle = {
	whiteSpace: 'normal',
	overflowWrap: 'anywhere',
} as const;

const bodyStyle = (isRefreshing: boolean) => css`
	min-width: 0;
	opacity: ${isRefreshing ? 0.55 : 1};
	transition: opacity ${theme.motion.duration};

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

type Props = {
	readonly title: string;
	readonly tooltip: string;
	/** One colour for a single band, several for a band split in equal segments. */
	readonly band: string | readonly string[];
	/** Right-aligned slot of the header, e.g. a filter. */
	readonly actions?: React.ReactNode;
	/** The body shows the previous answer while the current one loads: it is dimmed, never swapped. */
	readonly isRefreshing?: boolean;
	readonly children: React.ReactNode;
};

/** Shared chrome of an indicator block: inner colour band, eyebrow title and the definition bubble. */
const IndicatorCard: React.FC<Props> = ({
	title,
	tooltip,
	band,
	actions,
	isRefreshing = false,
	children,
}) => {
	const titleId = React.useId();
	const colors = typeof band === 'string' ? [band] : band;

	return (
		<section css={cardStyle} aria-labelledby={titleId} aria-busy={isRefreshing}>
			<div css={bandStyle} aria-hidden="true">
				{colors.map((color) => (
					<div key={color} css={bandSegmentStyle(color)} />
				))}
			</div>
			<div css={headerStyle}>
				<div css={titleRowStyle}>
					<h2 id={titleId} css={eyebrowTitleStyle}>
						{title}
					</h2>
					<Tooltip
						content={tooltip}
						position="top"
						event="both"
						closeOnEscape
						pt={{ root: { style: bubbleStyle }, text: { style: bubbleTextStyle } }}
					>
						<button type="button" css={infoButtonStyle} aria-label="Définition de l'indicateur">
							<Info size={16} aria-hidden="true" />
						</button>
					</Tooltip>
				</div>
				{actions}
			</div>
			<div css={bodyStyle(isRefreshing)}>
				{children}
				<span role="status">
					{isRefreshing && <VisuallyHiddenText>Mise à jour de l'indicateur…</VisuallyHiddenText>}
				</span>
			</div>
		</section>
	);
};

export default IndicatorCard;
